import { useTranslation } from "react-i18next"
import type { RoomPhase } from "@/api/queries/workplace"
import { StatusBadge, type StatusTone } from "@/components/app/status-badge"
import { UserAvatar } from "@/components/app/user-avatar"
import { cn } from "@/lib/utils"

export const PHASE_TONE: Record<RoomPhase, StatusTone> = {
  agent_running: "accent",
  awaiting_user_input: "warning",
  idle_active: "success",
  turn_budget_exhausted: "danger",
  paused: "neutral",
}

export function isPhase(v: string): v is RoomPhase {
  return v in PHASE_TONE
}

/** Run phase of a room (running / waiting for you / idle / budget reached / paused). */
export function PhaseBadge({ phase, className }: { phase: string; className?: string }) {
  const { t } = useTranslation()
  const p: RoomPhase = isPhase(phase) ? phase : "idle_active"
  return (
    <StatusBadge
      tone={PHASE_TONE[p]}
      dot
      pulse={p === "agent_running"}
      className={className}
      data-testid="room-phase"
    >
      {t(`workplace.phase.${p}`)}
    </StatusBadge>
  )
}

export const TASK_STATUSES = ["progress", "todo", "blocked", "failed", "done"] as const
export type TaskStatus = (typeof TASK_STATUSES)[number]

export const TASK_TONE: Record<TaskStatus, StatusTone> = {
  todo: "neutral",
  progress: "accent",
  done: "success",
  blocked: "warning",
  failed: "danger",
}

export function TaskStatusBadge({ status }: { status: string }) {
  const { t } = useTranslation()
  const s = (status in TASK_TONE ? status : "todo") as TaskStatus
  return (
    <StatusBadge tone={TASK_TONE[s]} dot pulse={s === "progress"}>
      {t(`workplace.taskStatus.${s}`)}
    </StatusBadge>
  )
}

/** Coloured initials for a room worker; a small dot marks the team leader. */
export function WorkerAvatar({
  name,
  leader,
  thinking,
  size = "default",
  className,
}: {
  name: string
  leader?: boolean
  thinking?: boolean
  size?: "sm" | "default"
  className?: string
}) {
  return (
    <span
      aria-hidden
      // self-start: in a stretched flex row the wrapper would grow to the message height
      // and the leader dot would sit at the bottom of the message instead of the avatar
      className={cn(
        "relative flex shrink-0 self-start rounded-full",
        thinking && "ring-primary ring-2",
        className,
      )}
    >
      <UserAvatar name={name} size={size === "sm" ? "sm" : "default"} />
      {leader && (
        <span className="bg-warning ring-background absolute -right-0.5 -bottom-0.5 size-2.5 rounded-full ring-2" />
      )}
    </span>
  )
}
